import React, { useEffect, useMemo, useState } from 'react';
import { cx } from '../shared/ui/utils';
import { CARD_RADIUS } from '../../lib/layout';
import styles from './ImageContainer.module.css';

const ImageContainer = ({
  src,
  alt,
  ratio = '16/9',
  radius = CARD_RADIUS,
  overlay = false,
  overlayStrength = 0.35,
  hoverZoom = true,
  priority = false,
  fit = 'cover',
  onLoad,
  onError,
  className,
  imgClassName,
  sizes,
  sources,
  srcSet,
  blurDataURL,
}) => {
  const [loaded, setLoaded] = useState(false);
  const safeAlt = typeof alt === 'string' ? alt : '';
  const ariaHidden = safeAlt === '';
  const normalizedSources = Array.isArray(sources) ? sources : [];
  const containerStyle = useMemo(() => {
    const style = { borderRadius: typeof radius === 'number' ? `${radius}px` : radius };
    if (ratio) style.aspectRatio = ratio;
    return style;
  }, [ratio, radius]);
  const blurStyle = blurDataURL ? { backgroundImage: `url(${blurDataURL})` } : undefined;

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <div className={cx(styles.container, hoverZoom && styles.hoverZoom, className)} style={containerStyle}>
      {!loaded && (
        <>
          {blurDataURL ? <div className={styles.blur} style={blurStyle} aria-hidden="true" /> : null}
          <div className={styles.shimmer} aria-hidden="true" />
        </>
      )}
      <picture className={styles.picture}>
        {normalizedSources.map((source, idx) => (
          <source
            key={`${source.type || 'source'}-${idx}`}
            type={source.type}
            srcSet={source.srcSet}
            sizes={source.sizes || sizes}
            media={source.media}
          />
        ))}
        <img
          src={src || ''}
          srcSet={srcSet}
          sizes={sizes}
          alt={safeAlt}
          aria-hidden={ariaHidden || undefined}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          style={{ objectFit: fit, objectPosition: 'center' }}
          className={cx(styles.image, loaded ? styles.loaded : styles.loading, imgClassName)}
          onLoad={(event) => {
            setLoaded(true);
            onLoad?.(event);
          }}
          onError={(event) => {
            setLoaded(true);
            onError?.(event);
          }}
        />
      </picture>
      {overlay ? (
        <div className={styles.overlay} style={{ opacity: overlayStrength }} aria-hidden="true" />
      ) : null}
    </div>
  );
};

export default ImageContainer;
